import * as React from "react"
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown"
import { FormattedMessage, IntlShape, useIntl } from "react-intl"
import moment, { Moment } from "moment-timezone"
import { useCallback } from "react"
import { TransportOrder, TransportOrderType } from "@/model/transportorder/transportOrder"
import { MenuItemProps } from "@rio-cloud/rio-uikit/MenuItem"
import { downloadEmptiesCsv } from "@/components/TransportOrder/CSVExport/emptiesCsvExport"
import { downloadMaterialsCsv } from "@/components/TransportOrder/CSVExport/materialCsvExport"

interface CSVExportButtonDropdownProps {
    fromDate: Date | undefined
    toDate: Date | undefined
    transportOrders: TransportOrder[]
}

export const CSVExportButtonDropdown = (props: CSVExportButtonDropdownProps): React.ReactElement | null => {
    const intl = useIntl()
    const filterByType = useCallback(
        (type: TransportOrderType): TransportOrder[] => props.transportOrders.filter((it) => type === it.orderType),
        [props.transportOrders]
    )

    const getEarliestFromDate = useCallback(
        (): Moment  => {
            let earliestDate = moment().startOf("day")
            props.transportOrders.forEach((it) => {
                const createdDate: Moment = moment(it.created_at).tz("UTC")

                if (createdDate.isBefore(earliestDate)) {
                    earliestDate = createdDate.tz(moment.tz.guess()).startOf("day")
                }
            })

            return earliestDate
        },
        [props.transportOrders]
    )

    const filteredTransportOrdersEmpties = filterByType(TransportOrderType.EMPTIES)
    const filteredTransportOrdersMaterial = filterByType(TransportOrderType.MATERIAL)

    const earliestFrom = getEarliestFromDate()
    const from = props.fromDate ? moment(props.fromDate) : earliestFrom
    const to = props.toDate ? moment(props.toDate) : moment().endOf("day")
    const formattedDateRange = `${from.format("YYYY-MM-DD_HHmm")}_${to.format("YYYY-MM-DD_HHmm")}`

    return <>
        <ButtonDropdown
            title={<>
                <span className="uikit-icon icon-document-out margin-right-5"/>
                <FormattedMessage id={"transportOrder.common.csvExport"}/>
            </>}
            items={[
                emptiesExportMenuItem(formattedDateRange, filteredTransportOrdersEmpties, intl),
                materialsExportMenuItem(formattedDateRange, filteredTransportOrdersMaterial, intl),
            ]}
            disabled={props.transportOrders.length === 0}
            className={"hover-text-color-white"}
        />
    </>
}

export const emptiesExportMenuItem = (formattedDateRange: string, transportOrders: TransportOrder[], intl: IntlShape): MenuItemProps => ({
    value: <div className={"text-color-darkest text-decoration-none hover-text-color-white"}>
        <FormattedMessage id={"transportOrder.csvexport.empties.buttonText"}/>
    </div>,
    onSelect: () => downloadEmptiesCsv(
        transportOrders,
        `exported_empties_transportorders_${formattedDateRange}.csv`,
        intl
    ),
})

const materialsExportMenuItem = (formattedDateRange: string, transportOrders: TransportOrder[], intl: IntlShape): MenuItemProps => ({
    value: <div className={"text-color-darkest text-decoration-none hover-text-color-white"}>
        <FormattedMessage id={"transportOrder.csvexport.material.buttonText"}/>
    </div>,
    onSelect: () => downloadMaterialsCsv(
        transportOrders,
        `exported_materials_transportorders_${formattedDateRange}.csv`,
        intl
    ),
})
